/**
 * loScoreboard.ts — pure aggregation for the LO Scoreboard report.
 *
 * Groups funded loans by the loan officer who is actually credited for the
 * production and ranks them by funded volume.
 *
 * Attribution policy (important): the Encompass `loan_officer_*` name is NOT
 * reliable — a large share of loans are entered under a single house/partner
 * account but are actually produced (and paid) by a different LO. The banker
 * recon payee (`banker_recons.lo_user_id`) is the source of truth. We therefore
 * credit each funded loan to its banker-recon payee where one exists (matched on
 * the stable `encompass_loan_id`), and only fall back to the Encompass name for
 * loans with no recon. An alias map folds known duplicate identities (e.g.
 * "Mohammad Nasim" -> "Tony Nasim").
 *
 * Reuses the canonical loan-type categorizer so the "loan mix" stays consistent
 * with the rest of the Funded reports.
 */
import { PipelineLoan } from './types';
import { getLoanTypeCategory } from './components/fundedSummaryModel';
import { emptyAttribution, groupScoreboardLoans, scoreboardVolume, type AttributionContext } from '../../../supabase/functions/_shared/lo-scoreboard';

// Compact tokens for the loan-mix string, keyed by getLoanTypeCategory output.
export const LOAN_MIX_SHORT_LABELS: Record<string, string> = {
  'Conventional: FNMA/FHLMC': 'Conv',
  'GNMA: FHA': 'FHA',
  'GNMA: VA': 'VA',
  'GNMA: USDA': 'USDA',
  'Non-Agency Jumbo': 'Jumbo',
  'Non-QM': 'Non-QM',
  'Second Liens': '2nd',
  'Other: CES / HELOC / Portfolio Loans': 'Other'
};

// Canonical order used to break count ties in the mix string.
const MIX_ORDER = [
  'Conventional: FNMA/FHLMC',
  'GNMA: FHA',
  'GNMA: VA',
  'GNMA: USDA',
  'Non-Agency Jumbo',
  'Non-QM',
  'Second Liens',
  'Other: CES / HELOC / Portfolio Loans'
];

export { normalizeLoKey, emptyAttribution, applyAlias, resolveCreditedLo,
  type CreditSource, type CreditedLo, type ReconPayee, type AttributionContext
} from '../../../supabase/functions/_shared/lo-scoreboard';
export interface LOScoreboardRow {
  rank: number;
  name: string;
  loans: number;
  volume: number;
  mix: string;
  /** ISO date string (users.created_at) or null when no user match. */
  joined: string | null;
}

/**
 * Build the compact loan-mix string, e.g. "28 Conv · 3 FHA · 3 Other".
 * Counts loans per canonical loan-type category, sorts by count desc
 * (ties broken by canonical order), and emits non-zero categories only.
 */
export function formatLoanMix(loans: PipelineLoan[]): string {
  const counts = new Map<string, number>();
  loans.forEach((loan) => {
    const cat = getLoanTypeCategory(loan);
    counts.set(cat, (counts.get(cat) || 0) + 1);
  });

  return [...counts.entries()]
    .filter(([, n]) => n > 0)
    .sort((a, b) => {
      if (b[1] !== a[1]) {
        return b[1] - a[1];
      }
      return MIX_ORDER.indexOf(a[0]) - MIX_ORDER.indexOf(b[0]);
    })
    .map(([cat, n]) => `${n} ${LOAN_MIX_SHORT_LABELS[cat] || cat}`)
    .join(' · ');
}

/**
 * Group funded loans by their credited loan officer and rank by total volume desc.
 * Grouping is by a normalized name key (case/whitespace-insensitive) so the same
 * LO never splits into two rows.
 *
 * @param loans  Already date-filtered funded loans.
 * @param ctx    Attribution context (recon payees, joined dates, aliases).
 */
export function buildLOScoreboard(
  loans: PipelineLoan[],
  ctx: AttributionContext = emptyAttribution()
): LOScoreboardRow[] {
  const groups = groupScoreboardLoans(loans, ctx);

  const rows = groups.map((group) => ({
    name: group.displayName,
    loans: group.loans.length,
    volume: scoreboardVolume(group.loans),
    mix: formatLoanMix(group.loans),
    joined: group.joined
  }));

  // Rank by volume desc, tiebreak loan count desc, then name asc.
  rows.sort((a, b) => {
    if (b.volume !== a.volume) {
      return b.volume - a.volume;
    }
    if (b.loans !== a.loans) {
      return b.loans - a.loans;
    }
    return a.name.localeCompare(b.name);
  });

  return rows.map((row, i) => ({ rank: i + 1, ...row }));
}
